<template>
  <div
    v-if="error"
    class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
    role="alert"
    >
    <strong class="font-bold">Error! </strong>
    <span class="block sm:inline">{{ error }}</span>
  </div>

  <HowToBinaries v-if="showHowToPage"
      :show-how-to-page="showHowToPage"
      :external-navigation="true"
      @show-how-to-close="handleShowHowToPage"
    />

  <div v-if="isStaff || canViewBinary" class="p-6">
    <!-- Header Section -->
    <div class="flex items-center justify-between mb-6">
      <h1 id="total_count" class="text-3xl font-bold text-gray-900 dark:text-white">
        Binaries - {{ filteredBinarys.length }}
        <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
      </h1>

      <div class="text-base flex space-x-3 justify-between">
        <button
            type="button"
            class="flex items-center gap-2 animate-shake hover:bg-gray-100 dark:hover:bg-gray-800 bg-transparent px-3 py-2 rounded-lg transition-colors"
            @click="startTutorial"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
              <path d="M12 17h.01"/>
            </svg>
            <span class="text-sm font-medium dark:text-gray-50 text-gray-900">Tutorial</span>
          </button>
          <button v-if="isStaff || canAddBinary"
          id="add"
            @click="
              $router.push({ name: 'BinaryDetails', params: { id: 'new' } })
            "
            class="btn bg-blue-700 text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Add Binary
          </button>
      </div>
    </div>

    <!-- Search and Filter Section -->
    <div
      class="flex flex-col md:flex-row md:items-center md:justify-between mb-6"
    >
      <!-- Search Bar -->
      <div class="mb-4 md:mb-0 md:flex-1 md:mr-4">
        <SearchBar
          v-model="searchQuery"
          placeholderText="Search binaries by name..."
        />
      </div>

      <div class="mb-4 md:hidden">
        <button
          @click="showFilters = !showFilters"
          class="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
        >
          {{ showFilters ? "Hide Filters" : "Show Filters" }}
        </button>
      </div>
      
      <!-- Filters Section -->
      <div
        :class="[
          'md:flex md:items-center ',
          showFilters ? 'block' : 'hidden md:block',
        ]"
      >
        <FiltersSection
          :filters="dynamicFilters"
          v-model:selectedFilters="selectedFilters"
          :showFilters="showFilters"
          @update:showFilters="showFilters = $event"
          @applyFilters="applyFilters"
        />
      </div>

      <!-- Bulk Action Section -->
      <div class="flex items-center mt-4 md:mt-0 space-x-2 ml-2">
        <GlassSelect
          id="bulkactions"
          v-model="bulkAction"
          :options="isStaff || canDeleteBinary ? [{ value: 'delete', label: 'Delete selected items' }] : []"
          placeholder="Bulk Actions"
          :valueKey="'value'"
          :labelKey="'label'"
        />
        <button
          id="apply"
          @click="applyBulkAction"
          :disabled="!bulkAction || selectedBinarys.length === 0"
          :class="[
            'btn focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200',
            bulkAction && selectedBinarys.length
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed',
          ]"
        >
          Apply
        </button>
      </div>
    </div>


    <!-- Binary Type Tabs -->
    <div class="mb-6">
      <div class="backdrop-blur-sm py-3 px-3 glass-card-lite">
        <nav class="flex flex-wrap items-center gap-3">
          <button
            v-for="tab in availableTabs"
            :key="tab.key"
            @click="selectTab(tab.key)"
            :class="[
              'bg-transparent px-4 py-2 font-bold transition-all duration-200 flex items-center gap-2 rounded-md border border-white glass-card whitespace-nowrap',
              selectedTab === tab.key
                ? 'text-fuchsia-500'
                : 'text-gray-400 hover:bg-slate-800/30 hover:text-fuchsia-500',
            ]"
          >
            {{ tab.label }}
            <span
              class="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-semibold"
              :class="selectedTab === tab.key
                ? 'bg-fuchsia-100 text-fuchsia-600 dark:bg-fuchsia-900/40 dark:text-fuchsia-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'"
            >
              {{ tab.key === 'all' ? baseFilteredBinaries.length : (tabCounts[tab.key] || 0) }}
            </span>
          </button>
        </nav>
      </div>
    </div>


    <GenericTable
      ref="table"
      :columns="columns"
      :items="paginatedBinarys"
      itemKey="id"
      :selectable="true"
      @update:selectedItems="updateSelectedBinarys"
      @rowClick="goToBinaryDetail"
    >
      <!-- Custom cell templates -->
      <template #binary_label="{ item }">
        <div class="flex items-center gap-2">
          <span>{{ item.binary_label }}</span>
          <span
            v-if="item.space_type"
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize"
            :class="item.space_type === 'workspace'
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
              : 'bg-emerald-100 text-emerald-700 dark:bg-yellow-900/40 dark:text-yellow-300'"
          >
            {{ item.space_type }}
          </span>
        </div>
      </template>
      <template #created_at="{ value }">
        {{ formatDateTime(value) }}
      </template>
      <template #last_modified="{ value }">
        {{ formatDateTime(value) }}
      </template>
    </GenericTable>

    <!-- Pagination -->
    <div
      class="flex flex-col md:flex-row items-center justify-between mt-8 space-y-4 md:space-y-0"
    >
      <div class="flex items-center space-x-2">
        <button
          @click="changePage(-1)"
          :disabled="currentPage === 1"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Previous
        </button>
        <button
          @click="changePage(1)"
          :disabled="currentPage === totalPages"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Next
        </button>
      </div>
      <div class="text-gray-700 dark:text-white">
        Page {{ currentPage }} of {{ totalPages }}
      </div>
      <div>
        <GlassSelect
          v-model="itemsPerPage"
          :options="[{ value: 10, label: '10 per page' }, { value: 20, label: '20 per page' }, { value: 50, label: '50 per page' }, { value: 100, label: '100 per page' }, { value: -1, label: 'All' }]"
          :valueKey="'value'"
          :labelKey="'label'"
          placeholder="Items per page"
          @change="updatePagination"
        />
      </div>
    </div>

    <DeleteModal
      v-if="deletingItem"
      :deleting-item="deletingItem"
      :componet-objects="componetObjects"
      :is-deleting="isDeleting"
      @delete="confirmDelete"
      @close="deletingItem=false"
    />
  </div>
</template>

<script>
import { getBinarys, deleteBinary } from "@/services/binaryService";
import { getOrgs } from "@/services/wsOrgService";
import { getAgents } from "@/services/agentService";
import { getAgentGroups } from "@/services/agentGroupService";
import SearchBar from "@/components/SearchBar.vue";
import GenericTable from "@/components/GenericTable.vue";
import { useStore } from "@/store";
import HowToBinaries from "./HowToBinaries.vue";
import FiltersSection from "@/components/FiltersSection.vue";
import DeleteModal from "@/components/DeleteModal.vue";
import { useTutorial } from "@/composables/tutorial";
import GlassSelect from "@/components/GlassSelect.vue";

export default {
  name: "BinaryView",
  setup() {
    const { start } = useTutorial();
    return { _start: start };
  },
  components: {
    SearchBar,
    GenericTable,
    HowToBinaries,
    FiltersSection,
    DeleteModal,
    GlassSelect,
  },
  data() {
    return {
      showFilters: false,
      showHowToPage: false,
      binarys: [],
      searchQuery: "",
      currentPage: 1,
      itemsPerPage: 10,
      selectedBinarys: [],
      deletingItem: false,
      bulkAction: "",
      columns: [
        { key: "binary_label", label: "Binary", sortable: true, width: "15%" },
        { key: "binary_scope", label: "Level",  sortable: true, width: "15%" },
        { key: "operating_system", label: "OS", sortable: true, width: "10%" },
        { key: "architecture", label: "Arch", sortable: true, width: "10%" },
        { key: "organization_name", label: "Organisation", sortable: true, width: "15%" },
        { key: "created_at", label: "Created Date", sortable: true, width: "10%" },
        { key: "last_modified", label: "Last Modified Date", sortable: true, width: "10%" },
      ],
      error: "",
      organizations: [],
      canAddBinary: false,
      canDeleteBinary: false,
      canViewBinary: false,
      dynamicFilters: [],
      selectedFilters: {
        organizationFilter: "",
        osFilter: "",
        spaceTypeFilter: "",
      },
      selectedTab: "all",
      binary_types : [
          { key:'workspace', label:'Workspace'},
          { key:'launcher', label:'Launcher'},
          { key:'collector', label:'Collector'},
          { key:'alloy', label:'Alloy'},
          { key:'alloyService', label:'Alloy Service'},
        ],
      steps: [
          {
            element: '#total_count',
            action: 'display',
            value: '',
            message: 'Total count of items in the table.'
          },
          {
            element: '#searchbar',
            action: 'display',
            value: '',
            message: 'Search and filter binaries.'
          },
          {
            element: '#table',
            action: 'display',
            value: '',
            message: 'Binary overview table displaying binary type and level (e.g group, organisation or agent).'
          },
          {
            element: '#bulkactions',
            action: 'display',
            value: 1,
            message: 'Select one or more items to enable bulk actions.'
          },
          {
            element: '#apply',
            action: 'display',
            message: 'Apply bulk actions (e.g., delete, archive, modify).'
          },
          {
            element: '#add',
            action: 'display',
            message: 'Add a new binary.'
          }
        ],
        isDeleting: false
  };
  },
  computed: {
    isStaff(){
      return useStore.isStaff();
    },
    user() {
      return useStore.getUser();
    }, 
    isFiltered() {
      return (
        this.searchQuery !== "" ||
        this.selectedFilters.organizationFilter !== "" ||
        this.selectedFilters.osFilter !== "" ||
        this.selectedFilters.spaceTypeFilter !== ""
      );
    },
    baseFilteredBinaries() {
        return this.binarys.filter((binary) => {
          const matchesSearch = (binary?.binary_label || "")
            .toLowerCase()
            .includes(this.searchQuery.toLowerCase());


          const matchesOrg =
            this.selectedFilters.organizationFilter === "" ||
            binary.organization === this.selectedFilters.organizationFilter;

          const matchesOs =
            this.selectedFilters.osFilter === "" ||
            binary.operating_system === this.selectedFilters.osFilter;

          const matchesLevel =
            this.selectedFilters.spaceTypeFilter=== "" ||
            binary.space_type === this.selectedFilters.spaceTypeFilter;

          return (
            matchesSearch &&
            matchesOrg &&
            matchesOs &&
            matchesLevel
          );
        });
      },
      filteredBinarys() {
        return this.selectedTab === "all"
          ? this.baseFilteredBinaries
          : this.baseFilteredBinaries.filter(
              (binary) => binary.binary_type === this.selectedTab
            );
      },
      availableTabs() {
        const allowed = this.binary_types
        return [{ key: "all", label: "All" }, ...allowed];
      },
      tabCounts() {
        const counts = {};
        for (const binary of this.baseFilteredBinaries) {
          counts[binary.binary_type] = (counts[binary.binary_type] || 0) + 1;
        }
        return counts;
      },
    totalPages() {
      return this.itemsPerPage === -1
        ? 1
        : Math.ceil(this.filteredBinarys.length / this.itemsPerPage);
    },
    paginatedBinarys() {
      if (this.itemsPerPage === -1) {
        return this.filteredBinarys;
      }
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredBinarys.slice(start, end);
    },
    componetObjects() {
      return this.selectedBinarys.map(binary => {
        return {
          values: [
            { name: "Binary", value: binary.binary_label || null },
            { name: "Space", value: binary.space_type || null },
            { name: "Organisation", value: binary.organization_name || null },
          ]
        };
      });
    },
  },
  watch: {
    searchQuery() {
      this.applyFilters();
    },
    user: {
      handler(user) {
        if (user) {
          this.checkPermissions();
        }
      },
      immediate: true
    },
  },
  async mounted() {
    this.initSelectedTab();
    this.checkPermissions();
    await this.fetchOrganisations();
    await this.fetchBinary();
    this.initializeFilters();
  },
  methods: {
    async startTutorial() {
      await this._start(this.steps);
    },
    initSelectedTab() {
      const tab = this.$route.query.tab;
      const validTab =
        tab === "all" || this.binary_types.some((t) => t.key === tab);
      this.selectedTab = validTab ? tab : "all";
    },
    specificTabUrl() {
      this.$router.push({
        name: "BinaryView",
        query: { tab: this.selectedTab }
      });
    },
    selectTab(key) {
      this.selectedTab = key;
      this.currentPage = 1;
      this.specificTabUrl();
    },
    handleShowHowToPage(value){
      this.showHowToPage = value;
    },
    checkPermissions(){
      this.canAddBinary = useStore.hasPermission("Can add binaries");
      this.canDeleteBinary = useStore.hasPermission("Can delete binaries");
      this.canViewBinary = useStore.hasPermission("Can view binaries");
    },
    async fetchOrganisations(){
      try {
        this.organizations = await getOrgs();
      } catch (error) {
        console.error("Error fetching organization:", error);
      }
    },
    async fetchBinary() {
      try {
          this.binarys = await getBinarys();
          const orgs = await getOrgs();
          const agents = await getAgents();
          const groups = await getAgentGroups();

          const orgMap = Object.fromEntries(orgs.map(org => [org.id, org.name]));
          const agentMap = Object.fromEntries(agents.map(agent => [agent.licensekey, agent.organization]));
          const groupMap = Object.fromEntries(groups.map(group => [group.id, group.organization]));

          this.binarys = this.binarys.map(binary => {
            let orgName = null;

            if (binary?.organization) orgName = orgMap[binary.organization];
            if (binary?.agent && agentMap[binary.agent]) orgName = orgMap[agentMap[binary.agent]];
            if (binary?.group && groupMap[binary.group]) orgName = orgMap[groupMap[binary.group]];

            let binary_scope = "unknown";
              if (binary.organization && !binary.group && !binary.agent) {
                binary_scope = `Organisation`;
              } else if (binary.organization && binary.group && !binary.agent) {
                binary_scope = `Group ${groups.find(item => item.id == binary.group)?.name}`;
              } else if (binary.organization && binary.agent) {
                binary_scope = `Agent ${agents.find(item => item.licensekey == binary.agent)?.licensekey}`;
              }
            
            return {
              ...binary,
              organization_name: orgName,
              binary_label: `${binary?.binary_type[0].toUpperCase()}${binary?.binary_type.slice(1)}`,
              binary_scope
            };
          })

        this.applyFilters();
      } catch (error) {
        console.error("Error fetching binarys:", error);
        if (error.response) {
          switch (error.response.status) {
            case 404:
              this.error = error.response.status +" The resource was not found. Please check the API endpoint."
              break;
            case 401:
              this.error = error.response.status + " Unauthorized access. Please check your authentication token or log in again."
              break;
            case 403:
              this.error = error.response.status + " You don't have permission to access this resource."
                          + " Contact a staff member to assign you the permissions to access this page. ";
              break;
            default:
              this.error = error.response.status =   error.response.data.detail || "Unknown error"
          }
        } else if (error.request) {
          alert(
            "No response received from the server. Please check your internet connection."
          );
        } else {
          alert(`An error occurred: ${error.message}`);
        }
      }
    },
    goToBinaryDetail(binary) {
      this.$router.push({
        name: "BinaryDetails",
        params: { id: binary.id },
      });
    },
    updateSelectedBinarys(selectedItems) {
      this.selectedBinarys = selectedItems;
    },
    async applyBulkAction() {
      if (this.bulkAction === "delete" ) {
        this.deletingItem = true;
      }
      this.bulkAction = "";
    },
    async confirmDelete() {
      this.isDeleting = true;
      await this.deleteSelectedBinarys();
      this.deletingItem = false; 
      this.bulkAction = "";
    },
    async deleteSelectedBinarys() {
      if (this.selectedBinarys.length === 0) {
        alert("No binarys selected for deletion");
        return;
      }
      try {
        const deletePromises = this.selectedBinarys.map((binary) =>
          deleteBinary(binary.id)
        );
        await Promise.all(deletePromises);

        this.binarys = this.binarys.filter(
          (binary) => !this.selectedBinarys.some((item) => item.id === binary.id)
        );
        
        this.$refs.table.clearSelection();
        this.applyFilters();
        this.selectedBinarys = [];
      } catch (error) {
        console.error("Error during bulk delete:", error);
        alert(
          "An error occurred while deleting organizations. Please try again."
        );
      } finally {
        this.isDeleting = false;
      }
    },
    formatDateTime(dateString) {
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    },
    applyFilters() {
      this.currentPage = 1;
    },
    changePage(delta) {
      this.currentPage += delta;
    },
    updatePagination() {
      this.currentPage = 1;
    },
    initializeFilters() {
        this.dynamicFilters = [
          {
            name: "organizationFilter",
            label: "Organisation",
            options: [{ value: "", label: "All" }].concat(
              Object.keys(this.organizations).map((orgId) => ({
                value: this.organizations[orgId]?.id,
                label: this.organizations[orgId]?.name,
              }))
            ),
          },
          {
            name: "osFilter",
            label: "OS",
            options: [
              { value: "", label: "All" },
              { value: "Windows", label: "Windows" },
              { value: "Linux", label: "Linux" },
            ],
          },
          {
            name: "spaceTypeFilter",
            label: "Space Type",
            options: [
              { value: "", label: "All" },
              { value: "workspace", label: "Workspace" },
              { value: "serverspace", label: "Serverspace" },
            ],
          },
        ];
      },
  },
};
</script>
